import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiHome, FiBarChart2, FiAlertCircle, FiPlus, FiSend, FiFileText, FiMessageCircle, FiArrowRight, FiTrendingUp, FiUsers, FiRadio, FiGlobe, FiX } from 'react-icons/fi';
import { IGlobalState } from '../../model/IGlobalState';
import ReactECharts from 'echarts-for-react';
import { DashboardOverviewResult, PriceBar } from '../../model/DashboardOverviewResult';
import api from '../../api/api';

const quickActions = (data: DashboardOverviewResult, ticker: string) => [
    { icon: <FiPlus />, title: 'Add an announcement', sub: 'Upload a PDF and email investors', url: `/client-stocks/${ticker}/Announcements?announcement=new` as string | null },
    { icon: <FiSend />, title: 'Send an email campaign', sub: 'Write a news update to subscribers', url: `/client-stocks/${ticker}/Email Marketing/Campaigns?campaign=new` },
    { icon: <FiFileText />, title: 'Run a holdings report', sub: 'See who bought or sold shares', url: `/client-stocks/${ticker}/Share Registry/Holdings Movement` },
    { icon: <FiMessageCircle />, title: 'Answer investor questions', sub: data.openQuestions ? `${data.openQuestions} open question${data.openQuestions === 1 ? '' : 's'}` : 'No open questions', url: `/AnnouncementComments/${ticker}` },
];

const ranges: Record<string, number> = { '1W': 7, '1M': 30, '3M': 91, '1Y': 365, '5Y': 1826 }; // days back; no intraday data so no 1D

const money = (v: number) => v.toFixed(v < 1 ? 3 : 2);
const compact = (v: number) => v >= 1e9 ? +(v / 1e9).toFixed(1) + 'B' : v >= 1e6 ? +(v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? +(v / 1e3).toFixed(1) + 'K' : String(v);

const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening'; };

const registryUpdated = (utc: string) => moment(utc).calendar(null, { sameDay: '[today], h:mm a', lastDay: '[yesterday], h:mm a', lastWeek: 'dddd, h:mm a', sameElse: 'D MMM, h:mm a' });

// same charting lib as the share-price widget (investi.client), styled with the v2 tokens
const PriceChart: React.FC<{ bars: PriceBar[] }> = ({ bars }) => {
    const option = {
        animation: false,
        grid: { left: 44, right: 12, top: 12, bottom: 28 },
        tooltip: {
            trigger: 'axis',
            backgroundColor: '#0b1739', borderWidth: 0, padding: [8, 12],
            textStyle: { color: '#fff', fontSize: 12 },
            axisPointer: { type: 'line', lineStyle: { color: '#0f172a', type: 'dashed' } },
            formatter: (params: any[]) => {
                const b = bars[params[0].dataIndex];
                return `<strong>${moment(b.date).format('D MMM')} · ${money(b.close)}</strong><br/><span style="color:#94a3b8;font-size:11px">Vol ${compact(b.volume)}</span>`;
            },
        },
        xAxis: {
            type: 'category', boundaryGap: false, data: bars.map(b => b.date),
            axisLine: { show: false }, axisTick: { show: false },
            axisLabel: { color: '#64748b', fontSize: 12, formatter: (d: string) => moment(d).format('D MMM') },
        },
        yAxis: {
            type: 'value', scale: true,
            splitLine: { lineStyle: { color: '#e5e9f0', type: 'dashed' } },
            axisLabel: { color: '#64748b', fontSize: 12, formatter: (v: number) => money(v) },
        },
        series: [{
            type: 'line', showSymbol: false, data: bars.map(b => b.close),
            lineStyle: { color: '#00B5D1', width: 2.5 }, itemStyle: { color: '#00B5D1' },
            areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(0,181,209,0.35)' }, { offset: 1, color: 'rgba(0,181,209,0)' }] } },
        }],
    };
    return <ReactECharts option={option} notMerge={true} style={{ height: 210 }} />;
};

const Dashboard: React.FC = () => {
    const user = useSelector((state: IGlobalState) => state.user);
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [range, setRange] = useState('1M');
    const [data, setData] = useState<DashboardOverviewResult | null>(null);
    const history = useHistory();
    // relative url = in-app route (same tab); absolute = external page (new tab)
    const open = (url: string | null) => { if (url) url.startsWith('/') ? history.push(url) : window.open(url, '_blank'); };
    const ticker = `${stock?.ticker}:${stock?.exchange}`;
    // glance tile destinations, keyed on the tile's link label; tiles without one are inert until their page exists
    const tileUrl: Record<string, string> = {
        'Open Registry': '/share-registry',
        'See Announcements': `/client-stocks/${ticker}/Announcements`,
        'Open Campaigns': `/client-stocks/${ticker}/Email Marketing/Campaigns`,
        'Open Settings': `/client-stocks/${ticker}/Config`,
    };
    const firstName = user.username.split(/[@.]/)[0];
    const name = firstName.charAt(0).toUpperCase() + firstName.slice(1);

    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!stock) return;
        const result = await api().getDashboardOverview(stock.id);
        if (isMounted()) setData(result);
    }, [stock?.id]);

    const dismiss = async (key: string) => {
        if (!stock || !data) return;
        setData({ ...data, attention: data.attention.filter(x => x.key !== key) });
        await api().dismissAttention(stock.id, key);
    };

    const sp = data?.sharePrice;
    const up = (sp?.changePct ?? 0) >= 0;
    const since = moment().subtract(ranges[range], 'days').format('YYYY-MM-DD');
    const bars = (sp?.bars ?? []).filter(b => b.date >= since);

    return <div className="v2-page dashboard-v2">
        <div className="sub-tabs">
            <button type="button" className="active"><FiHome /> Overview</button>
            <button type="button"><FiBarChart2 /> Analytics</button>
        </div>

        <div className="page-header">
            <h1>{greeting()}, {name}</h1>
            <p>{moment().format('dddd, D MMMM YYYY')}{stock && ` · ${stock.companyName} (${stock.ticker}:${stock.exchange})`}{data?.registryLastUpdated && ` · Registry last updated ${registryUpdated(data.registryLastUpdated)}`}</p>
        </div>

        {data && sp && <>
        <div className="row row-0">
            <section className="card attention">
                <header>
                    <span className="icon-tile"><FiAlertCircle /></span>
                    <h2>Needs Your Attention</h2>
                    <span className="pill warn">{data.attention.length ? `${data.attention.length} ${data.attention.length === 1 ? 'thing' : 'things'} to do today` : 'You’re all caught up'}</span>
                </header>
                {data.attention.map(x => <div className="item" key={x.key ?? x.title}>
                    <div className="text"><div className="title">{x.title}</div><div className="sub">{x.detail}</div></div>
                    <button type="button" className="btn-outline" onClick={() => open(x.url)}>{x.action}</button>
                    {x.key && <button type="button" className="dismiss" title="Dismiss" onClick={() => dismiss(x.key!)}><FiX /></button>}
                </div>)}
            </section>

            <section className="card quick-actions">
                <h2>Quick Actions</h2>
                <p className="sub">The things you do most often</p>
                {quickActions(data, ticker).map(x => <button type="button" className="action" key={x.title} onClick={() => open(x.url)}>
                    <span className="icon-tile dark">{x.icon}</span>
                    <span className="text"><span className="title">{x.title}</span><span className="sub">{x.sub}</span></span>
                    <FiArrowRight className="arrow" />
                </button>)}
            </section>
        </div>

        <section className="glance">
            <img className="glance-bg" src="/images/login/brand-panel.jpg" alt="" />
            <h2>Your workspace at a glance</h2>
            <div className="grid">
                {data.glance.map(x => <div className="stat" key={x.label}>
                    <div className="value">{x.value} <span className="label">{x.label}</span></div>
                    <div className="sub">{x.detail}</div>
                    <button type="button" className="link" onClick={() => open(tileUrl[x.link] ?? null)}>{x.link} <FiArrowRight /></button>
                </div>)}
            </div>
        </section>

        <div className="row row-2">
            <section className="card share-price">
                <header>
                    <span className="icon-tile"><FiTrendingUp /></span>
                    <h2>{sp.title}</h2>
                </header>
                <div className="price-row">
                    {sp.stats.length === 0 ? <span className="price">—</span> : <>
                        <span className="price">{money(sp.price)}</span>
                        <span className={'pill ' + (up ? 'ok' : 'warn')}>{up ? '▲' : '▼'} {Math.abs(sp.changePct).toFixed(1)}% ({up ? '+' : '-'}{money(Math.abs(sp.changeAbs))})</span>
                        <span className="delay">{sp.delay}</span>
                    </>}
                    <div className="ranges">{Object.keys(ranges).map(r => <button type="button" key={r} className={r === range ? 'active' : ''} onClick={() => setRange(r)}>{r}</button>)}</div>
                </div>
                {bars.length > 1 ? <PriceChart bars={bars} /> : <p className="sub">No price history yet</p>}
                <div className="stats">{sp.stats.map(x => <div className="stat" key={x.label}><span className="k">{x.label}</span><span className="v">{x.value}</span></div>)}</div>
            </section>

            <section className="card top-holders">
                <header>
                    <span className="icon-tile"><FiUsers /></span>
                    <h2>Top Shareholders</h2>
                    <button type="button" className="link" onClick={() => open('/share-registry')}>All {data.topHolders.total} <FiArrowRight /></button>
                </header>
                {data.topHolders.items.length === 0 && <p className="sub">No registry data yet</p>}
                {data.topHolders.items.map((x, i) => <div className="holder" key={i}>
                    <span className="rank">{x.rank}</span>
                    <span className="name">{x.name}</span>
                    <span className="pct">{x.pct.toFixed(1)}%</span>
                    <span className="bar"><span style={{ width: `${(x.pct / (data.topHolders.items[0].pct || 1)) * 100}%` }} /></span>
                </div>)}
            </section>
        </div>

        <div className="row row-3">
            <section className="card announcements">
                <header>
                    <span className="icon-tile"><FiRadio /></span>
                    <h2>Recent Announcements</h2>
                    <button type="button" className="link" onClick={() => open(`/client-stocks/${ticker}/Announcements`)}>All {data.announcements.total} <FiArrowRight /></button>
                </header>
                {data.announcements.items.map((x, i) => <div className="item" key={i}>
                    <div className="text">
                        <div className="title">{x.headline}</div>
                        <div className={'sub' + (x.priceSensitive ? ' sensitive' : '')}>{x.when}{x.priceSensitive && <> · Price sensitive</>}</div>
                    </div>
                    <span className={'pill ' + x.tone}>{x.badge}</span>
                </div>)}
            </section>

            <section className="card investors">
                <header>
                    <span className="icon-tile"><FiGlobe /></span>
                    <h2>Latest from Investors</h2>
                    <button type="button" className="link" onClick={() => open(`/AnnouncementComments/${ticker}`)}>More <FiArrowRight /></button>
                </header>
                {data.investors.map((x, i) => <div className="item" key={i}>
                    <div className="title">{x.text}</div>
                    <div className="sub">{moment(x.when).fromNow()} · {x.meta}</div>
                </div>)}
                {data.investors.length === 0 && <div className="item"><div className="sub">No investor activity yet</div></div>}
            </section>
        </div>
        </>}
    </div>;
};

export default Dashboard;
